import type { StockImportLogModel } from '../types/stockImports.types'
import type {
  MissingSkuIssueType,
  PriceCancelReason,
  PriceCutReason,
  PriceIssueReason,
  SegmentPriceCutDto,
  SegmentPriceMode,
} from '../types/stockImportsPrices.types'

export const PRICE_ISSUE_REASON_LABELS: Record<PriceIssueReason, string> = {
  PRICE_EMPTY: 'Нет закупочной цены',
  PRICE_NOT_NUMBER: 'Закупочная цена — не число',
  PRICE_NON_POSITIVE: 'Закупочная цена 0 или меньше',
  CURRENCY_UNKNOWN: 'Валюта не распознана',
  CURRENCY_RATE_MISSING: 'Нет курса валюты на сегодня',
  RRC_INVALID: 'Нет РРЦ, а по ней считается сегмент',
  COLUMN_INVALID: 'Нет цены в колонке сегмента',
}

export const PRICE_CUT_REASON_LABELS: Record<PriceCutReason, string> = {
  BELOW_FLOOR: 'Ниже минимальной наценки',
  ABOVE_RRC: 'Выше РРЦ',
}

export const PRICE_CANCEL_REASON_LABELS: Record<PriceCancelReason, string> = {
  HEADER_MISMATCH: 'Заголовок ценовой колонки не совпал с ожидаемым',
  BASE_PRICE_MISMATCH:
    'Закупка из файла в разы отличается от «Базовой» цены УТ: проверьте валюту, НДС и колонку закупки',
  MASS_SPIKE:
    'Закупка резко изменилась у половины позиций и больше: похоже на сдвиг колонок',
}

export const SEGMENT_PRICE_MODE_LABELS: Record<
  SegmentPriceMode,
  { label: string; hint: string }
> = {
  MARKUP: {
    label: 'Наценка к закупке',
    hint: 'Цена = закупка × (1 + наценка). Наценка = наценка сегмента по умолчанию (если отмечено) + добавка',
  },
  RRC_DISCOUNT: {
    label: 'Скидка от РРЦ',
    hint: 'Цена = РРЦ × (1 − скидка). Нужна колонка РРЦ',
  },
  RRC_COLUMN: {
    label: 'РРЦ из файла',
    hint: 'Только для сегмента РРЦ. Цена = РРЦ из файла без НДС',
  },
  COLUMN: {
    label: 'Своя колонка',
    hint: 'Цена берётся из указанной колонки файла',
  },
}

export const MISSING_SKU_ISSUE_TYPE_OPTIONS: Array<{
  value: MissingSkuIssueType | 'ALL'
  label: string
}> = [
  { value: 'STOCK', label: 'Остатки' },
  { value: 'PRICE', label: 'Цены' },
  { value: 'ALL', label: 'Все' },
]

const RATIO_FRACTION_DIGITS = 2

export const formatPriceRatio = (value: number | undefined) =>
  value === undefined
    ? '—'
    : value.toFixed(RATIO_FRACTION_DIGITS).replace('.', ',')

export const formatPriceCuts = (cuts: SegmentPriceCutDto[] | undefined) =>
  (cuts ?? [])
    .map(
      (cut) =>
        `${cut.segmentDescription ?? cut.segmentCode}: ${PRICE_CUT_REASON_LABELS[cut.reason]}`
    )
    .join('; ')

export type PriceLogCounterField = keyof Pick<
  StockImportLogModel,
  | 'pricedRows'
  | 'priceInvalidRows'
  | 'priceCutFloorRows'
  | 'priceCutRrcRows'
  | 'priceSpikeRows'
  | 'priceIssueRows'
>

export const PRICE_LOG_COUNTERS: Array<{
  field: PriceLogCounterField
  label: string
}> = [
  { field: 'pricedRows', label: 'С ценой' },
  { field: 'priceInvalidRows', label: 'Без закупки' },
  { field: 'priceCutFloorRows', label: 'Ниже мин. наценки' },
  { field: 'priceCutRrcRows', label: 'Выше РРЦ' },
  { field: 'priceSpikeRows', label: 'Скачок закупки' },
  { field: 'priceIssueRows', label: 'Проблемы цен' },
]
